import React, { useMemo } from 'react';
import styles from './OrderDetail.module.scss';
import { Order } from 'types';
import { isArray } from 'lodash';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  PackageIcon,
  UserIcon,
  LocationIcon,
  AttemptsIcon,
  CalendarIcon,
  DownIcon,
} from './icons';
import PriorityFlag from 'components/PriorityFlag';
import { WrenchIcon } from 'pages/Signup/icons';
import { getFullName } from 'helpers/user';

interface JobDetailsContentProps {
  order?: Order;
  handleAddressClick: () => void;
}

const OrderDetail: React.FC<JobDetailsContentProps> = ({
  order = {} as Order,
  handleAddressClick,
}) => {
  const jobService = useMemo(() => {
    if (isArray(order.orders) && order.orders?.length! > 0) {
      return order.orders?.find(
        (order) => (order.items[0] as any)?.drug?.offering_type === 'service'
      );
    }
    return null;
  }, [order]);
  const priorities = useMemo(
    () =>
      order?.orders
        .filter((order) => !!order.priority)
        .map((order) => order.priority),
    [order]
  );
  const hasPriority = !!priorities.length;
  const includedProducts = useMemo(() => {
    if (isArray(order.orders) && order.orders?.length! > 0) {
      return order.orders?.filter(
        (order) => order.items[0]?.drug?.offering_type === 'product'
      );
    }
    return [];
  }, [order]);
  const isDeliveryOnWay = order?.status === 'on_way';
  const isVendor = order?.customer?.client_role === 'vendors';
  const getOrderNumber = () => {
    return order?.orders?.[0]?.sequential_order_number?.toString() || 'N/A';
  };

  const getPriority = () => {
    if (!priorities.length) {
      return 'No Priority';
    }
    if (priorities.includes('high')) {
      return 'High Priority';
    }
    if (priorities.includes('medium')) {
      return 'Medium Priority';
    }
    if (priorities.includes('low')) {
      return 'Low Priority';
    }
  };

  const getPreferenceDate = () => {
    if (isArray(order.orders) && order.orders?.length! > 0) {
      const firstOrder = jobService?.items[0];

      if (firstOrder?.preference_date) {
        return firstOrder.preference_date;
      }
    }
    return format(new Date(), 'MM/dd/yy');
  };
  const getLocation = () => {
    if (order.delivery_address) {
      if (typeof order.delivery_address === 'string') {
        return order.delivery_address;
      }
      return (
        (order.delivery_address as any)?.display_address ||
        `${(order.delivery_address as any)?.address_line_1 || ''}, ${(order.delivery_address as any)?.city || ''
        }, ${(order.delivery_address as any)?.state || ''}`
      );
    }
    return 'Location not specified';
  };

  const getAttempts = () => {
    if (isArray(order.orders) && order.orders?.length! > 0) {
      return jobService?.attempt || 0;
    }
    return 0;
  };

  const getCustomerName = () => {
    if (isVendor) {
      return order.customer?.customer_company;
    }
    return getFullName(order.customer?.user);
  };

  return (
    <div className={styles.jobDetailsContent}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <div className={styles.tags}>
            <Link to={`/orders/${getOrderNumber()}`}>
              <span className={styles.orderTag}>Order #{getOrderNumber()}</span>
            </Link>
          </div>
        </div>
        {hasPriority && (
          <div className={styles.prioritySection}>
            <span className={styles.priorityTag}>
              <PriorityFlag priority={priorities[0] as any} />
              {getPriority()}
            </span>
          </div>
        )}
      </div>

      {/* Details Section */}
      <div className={styles.detailsSection}>
        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>
              <UserIcon className={styles.detailIcon} />
              Customer
            </div>
            <div className={styles.detailValue}>{getCustomerName()}</div>
          </div>

          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>
              <AttemptsIcon className={styles.detailIcon} />
              Attempts
            </div>
            <div className={styles.detailValue}>{getAttempts()}</div>
          </div>
          <div className={styles.detailItem}>
            <div className={styles.detailLabel}>
              <CalendarIcon className={styles.detailIcon} />
              Preference Date
            </div>
            <div className={styles.detailValue}>{getPreferenceDate()}</div>
          </div>
        </div>
        <div
          className={classNames(styles.detailItem, styles.addressSelect)}
          onClick={isDeliveryOnWay ? handleAddressClick : undefined}
        >
          <div className={styles.detailLabel}>
            <LocationIcon className={styles.detailIcon} />
            Location
          </div>
          <div
            className={classNames(
              styles.detailValue,
              !isDeliveryOnWay && styles.disabled
            )}
          >
            {getLocation()}{' '}
            <span className={styles.addressSelectIcon}>
              <DownIcon className={styles.detailIcon} width={16} height={16} />
            </span>
          </div>
        </div>
      </div>

      {jobService && (
        <div className={styles.jobOverview}>
          <div className={styles.sectionHeader}>
            <WrenchIcon className={styles.packageIcon} />
            <h2 className={styles.sectionTitle}>Service</h2>
          </div>
          <div className={styles.productCards}>
            <Link
              to={`/orders/${jobService.sequential_order_number}`}
              key={jobService.id}
            >
              <div className={styles.productCard}>
                <div>
                  <h4>{jobService.items[0]?.drug?.name}</h4>
                  {jobService.items[0]?.drug?.description && (
                    <p className={styles.description}>
                      {jobService.items[0]?.drug?.description}
                    </p>
                  )}
                </div>
                <div className={styles.price}>
                  ${jobService.items[0]?.drug?.price}
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* Included products section */}
      {includedProducts.length > 0 && (
        <div className={styles.jobOverview}>
          <div className={styles.sectionHeader}>
            <PackageIcon className={styles.packageIcon} />
            <h2 className={styles.sectionTitle}>
              Products ({includedProducts.length})
            </h2>
          </div>
          <div className={styles.productCards}>
            {includedProducts.map((product) => (
              <Link
                to={`/orders/${product.sequential_order_number}`}
                key={product.id}
              >
                <div className={styles.productCard}>
                  <div>
                    <h4>{product.items[0]?.drug?.name}</h4>
                    {product.items[0]?.drug?.description && (
                      <p className={styles.description}>
                        {product.items[0]?.drug?.description}
                      </p>
                    )}
                  </div>
                  <div className={styles.price}>
                    ${product.items[0]?.drug?.price}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;
